import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useUserDetailQuery } from '@/hook';
import { VButton, VFlex } from '@/package';
import {
  useBoxClipboardStore,
  useLabelingBoxStore,
  useTrackIDChangeRequestStore,
} from '@/store';

import { EditLinkedTrackIDPanel } from './EditLinkedTrackIDPanel';
import type { LinkedFrameItemData } from './type';
import { useEditLinkedTrackIDShared } from './useEditLinkedTrackIDShared';

export const EditLinkedTrackIDPopup = () => {
  const { t } = useTranslation();
  const clearRequest = useTrackIDChangeRequestStore(
    state => state.clearRequest,
  );
  const copiedTrackIDByClipboard = useBoxClipboardStore(
    state => state.copiedTrackID ?? '',
  );
  const navigate = useNavigate();
  const requestUserID = useTrackIDChangeRequestStore(
    state => state.requestUserID,
  );
  const requestedTrackID = useTrackIDChangeRequestStore(
    state => state.targetTrackID,
  );
  const requestedUpdateTrackID = useTrackIDChangeRequestStore(
    state => state.nextTrackID,
  );
  const copiedTrackID = requestedUpdateTrackID ?? copiedTrackIDByClipboard;
  const {
    boxList,
    currentFrame,
    excludedFrameSeqSet,
    formState,
    getValues,
    handleApply: handleApplyByShared,
    handleRemoveItem,
    handleRemoveList,
    hasNotAssignedFrame,
    hide,
    linkedFrameListGroup,
    pcdIndexBoxListMap,
    register,
    show,
    totalFrameCount,
    visible,
  } = useEditLinkedTrackIDShared({
    copiedTrackID,
    setValueShouldDirty: true,
  });
  const { data: userDetail } = useUserDetailQuery(requestUserID ?? '');
  const requestUserName = userDetail?.name || userDetail?.email?.split('@')[0];

  const hidePopup = () => {
    clearRequest();
    hide();
  };

  const handleApply = (frameList?: LinkedFrameItemData[]) => {
    return handleApplyByShared(frameList).then(handleApplyAllCancel);
  };

  const handleApplyAll = () => {
    return Promise.all(linkedFrameListGroup.map(handleApply));
  };

  const handleApplyAllCancel = () => {
    navigate(
      `/labeling/${currentFrame?.projectID}/${currentFrame?.taskID}/${currentFrame?.frameSeq}`,
    );
    hidePopup();
  };

  const handleNavigateToFrame = (frameItem: LinkedFrameItemData) => {
    navigate(
      `/labeling/${currentFrame?.projectID}/${frameItem.taskID}/${frameItem.frameSeq}`,
      { replace: true },
    );
    useTrackIDChangeRequestStore.getState().clearRequest();
    hidePopup();
  };

  useEffect(() => {
    if (!requestedTrackID) {
      return;
    }

    useLabelingBoxStore.getState().selectBoxByTrackID(requestedTrackID);
    show();
  }, [pcdIndexBoxListMap, requestedTrackID, show]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          animate="visible"
          className="absolute top-2 right-4 w-[424px] rounded-md bg-white px-4 py-2 shadow-sm"
          exit="hidden"
          initial="hidden"
          role="dialog"
          transition={{
            duration: 250 / 1e3,
            ease: [0.23, 1, 0.32, 1],
          }}
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1 },
          }}
        >
          <EditLinkedTrackIDPanel
            boxList={boxList}
            excludedFrameSeqSet={excludedFrameSeqSet}
            formState={formState}
            getValues={getValues}
            hasNotAssignedFrame={hasNotAssignedFrame}
            linkedFrameListGroup={linkedFrameListGroup}
            listContainerClassName="h-fit max-h-[336px] flex-1 overflow-y-auto"
            panelTitle={t(
              'labeling.editLinkedTrackIDPopupRequestTitle.melilitite',
            )}
            register={register}
            requestUserName={requestUserName}
            totalFrameCount={totalFrameCount}
            onApply={handleApply}
            onClose={hidePopup}
            onNavigate={handleNavigateToFrame}
            onRemoveItem={handleRemoveItem}
            onRemoveList={handleRemoveList}
          />

          <VFlex className="py-2" gap={0.25} items="center" justify="end">
            <VButton
              outlined
              variant="secondary"
              onClick={handleApplyAllCancel}
            >
              {t(
                'labeling.editLinkedTrackIDPopupCancelApplyAllButtonText.hackery',
              )}
            </VButton>
            <VButton onClick={handleApplyAll}>
              {t(
                'labeling.editLinkedTrackIDModalApplyAllButtonText.repercussiveness',
              )}
            </VButton>
          </VFlex>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

EditLinkedTrackIDPopup.displayName = 'EditLinkedTrackIDPopup';
